import React, { useState, useEffect } from 'react';
import { Users, Search, Building2, ArrowRight, CheckCircle, Shield, Sparkles, AlertCircle, X, Calendar, User, Mail, Phone } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getOrganizationProfile } from '../../lib/supabase/organization';
import { getContacts, getOrganizations } from '../../lib/supabase/contacts';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CompanySetupForm from '../../components/chat/CompanySetupForm';
import ContactForm from '../../components/operations/contacts/ContactForm';
import ContactList from '../../components/operations/contacts/ContactList';
import ContactDetails from '../../components/operations/contacts/ContactDetails';
import EditContactForm from '../../components/operations/contacts/EditContactForm';
import EditOrganizationForm from '../../components/operations/contacts/EditOrganizationForm';
import { useEntity } from '../../hooks/useEntity';
import type { Contact, Organization } from '../../types/contacts';

export default function ContactManagerHome() {
  const { user } = useAuthStore();
  const { entity } = useEntity();
  const navigate = useNavigate();
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showOrganizations, setShowOrganizations] = useState(false);
  const [showRecentActivity, setShowRecentActivity] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [editingOrganizationId, setEditingOrganizationId] = useState<string | null>(null);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [recentActivity, setRecentActivity] = useState<Array<{
    id: string;
    type: 'contact_added' | 'contact_updated' | 'organization_added';
    title: string;
    description: string;
    timestamp: Date;
  }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  useEffect(() => {
    checkProfile();
    loadRecentActivity();
  }, []);

  useEffect(() => {
    if (entity?.id && hasProfile) {
      loadOrganizations();
    }
  }, [entity?.id, hasProfile, refreshTrigger]);
  const checkProfile = async () => {
    try {
      setIsCheckingProfile(true);
      setIsLoading(true);
      const profile = await getOrganizationProfile();
      setHasProfile(!!profile);
    } catch (error) {
      console.error('Failed to check profile:', error);
      setHasProfile(false);
    } finally {
      setIsLoading(false);
      setIsCheckingProfile(false);
    }
  };

  const handleAddContact = () => {
    if (!user) {
      navigate('/contactmanager/login');
      return;
    }
    
    // Only show company form if we're sure there's no profile and not currently checking
    if (hasProfile === false && !isCheckingProfile) {
      setShowCompanyForm(true);
    } else if (hasProfile === true) {
      setShowContactForm(true);
    }
  };

  const handleCompanySetupComplete = () => {
    setShowCompanyForm(false);
    setHasProfile(true);
    setRefreshTrigger(prev => prev + 1);
    // Force a small delay to ensure the contact is saved before refreshing
    setTimeout(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 500);
    setShowContactForm(true);
  };

  const handleSkipSetup = () => {
    setShowCompanyForm(false);
    setHasProfile(true);
    setShowContactForm(true);
  };

  const handleSearch = async () => {
    if (!entity?.id || !searchTerm.trim()) return;
    
    try {
      setSearchLoading(true);
      const contacts = await getContacts(entity.id);
      const filtered = contacts.filter(contact => 
        `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.emails?.some(email => email.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        contact.phones?.some(phone => phone.phone.includes(searchTerm)) ||
        contact.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadOrganizations = async () => {
    if (!entity?.id) return;
    
    try {
      setOrgLoading(true);
      const orgs = await getOrganizations(entity.id);
      setOrganizations(orgs || []);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setOrgLoading(false);
    }
  };

  const loadRecentActivity = () => {
    // Simulate more recent activity - in a real app this would come from an activity log
    const activities = [
      {
        id: '1',
        type: 'contact_added' as const,
        title: 'New contact added',
        description: 'John Doe was added to your contacts',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 hours ago
      },
      {
        id: '2',
        type: 'contact_updated' as const,
        title: 'Contact updated',
        description: 'Jane Smith\'s information was updated',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000) // 5 hours ago
      },
      {
        id: '3',
        type: 'organization_added' as const,
        title: 'Organization added',
        description: 'Acme Corp was added to organizations',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000) // 1 day ago
      },
      {
        id: '4',
        type: 'contact_added' as const,
        title: 'New contact added',
        description: 'Sarah Wilson was added to your contacts',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) // 2 days ago
      },
      {
        id: '5',
        type: 'contact_updated' as const,
        title: 'Contact updated',
        description: 'Mike Johnson\'s phone number was updated',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) // 3 days ago
      },
      {
        id: '6',
        type: 'organization_added' as const,
        title: 'Organization added',
        description: 'Tech Solutions Inc was added to organizations',
        timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000) // 4 days ago
      },
      {
        id: '7',
        type: 'contact_added' as const,
        title: 'New contact added',
        description: 'Lisa Brown was added to your contacts',
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000) // 5 days ago
      }
    ];
    setRecentActivity(activities);
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  if (isLoading || isCheckingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Header department="Contact Manager" />
      
      <main className="pt-24 px-4">
        {!hasProfile && !isLoading && (
          <div className="max-w-7xl mx-auto mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <Building2 className="w-12 h-12 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">
                Set Up Your Organization First
              </h2>
              <p className="text-yellow-700 mb-4">
                Before you can add contacts, we need to set up your organization profile. 
                This helps us organize your contacts properly.
              </p>
              <button 
                onClick={() => setShowCompanyForm(true)}
                className="bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Set Up Organization
              </button>
            </div>
          </div>
        )}
        
        {hasProfile && (
          <div className="max-w-7xl mx-auto mb-6">
            <button 
              onClick={handleAddContact}
              className="flex items-center justify-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Users className="w-5 h-5" />
              <span>Add New Contact</span>
            </button>
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          {/* Contact List */}
          <div className="mb-12">
            <ContactList refreshTrigger={refreshTrigger} />
          </div>

          {/* Welcome Section */}
          {hasProfile && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Welcome to Contact Manager</h1>
                  <p className="text-gray-600">Your AI-powered contact organization system</p>
                </div>
              </div>
              
              <p className="text-lg text-gray-700 mb-6">
                Hi {user?.name || 'there'}, I'm your Contact Management Assistant. I'll help you keep your contacts organized and up-to-date.
                This is your free Contact Manager - a simple way to organize all your business contacts in one place.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={handleAddContact}
                  className="flex items-center justify-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span>Add New Contact</span>
                </button>
                <button className="flex items-center justify-center space-x-2 border-2 border-blue-500 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors">
                  <ArrowRight className="w-5 h-5" />
                  <span>Import Contacts</span>
                </button>
                <div className="relative group">
                  <button 
                    onClick={() => window.location.href = '/'}
                    className="flex items-center justify-center space-x-2 border-2 border-green-500 text-green-600 px-6 py-3 rounded-lg hover:bg-green-50 transition-colors w-full"
                  >
                    <Sparkles className="w-5 h-5" />
                    <span>Upgrade to Full Gekko</span>
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-10">
                    <div className="text-center">
                      <p className="font-medium mb-1">Full Gekko HQ</p>
                      <p>Your complete AI business management platform with specialized AI assistants for sales, projects, operations, and business development - all working together to grow your business.</p>
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {!hasProfile && !isLoading && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="text-center">
                <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-blue-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Contact Manager</h1>
                <p className="text-lg text-gray-700 mb-6">
                  Hi {user?.name || 'there'}! To get started with Contact Manager, we need to set up your organization profile first. 
                  This helps us organize your contacts properly and ensures everything works smoothly.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div className="text-left">
                      <h3 className="font-medium text-blue-900 mb-1">Quick Setup Required</h3>
                      <p className="text-blue-700 text-sm">
                        Just enter your organization name to get started. You can add more details later.
                      </p>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCompanyForm(true)}
                  className="bg-blue-500 text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors text-lg font-medium"
                >
                  Set Up My Organization
                </button>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {hasProfile && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <button 
                onClick={() => setShowSearch(true)}
                className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow text-left"
              >
                <Search className="w-6 h-6 text-blue-500 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Search Contacts</h3>
                <p className="text-gray-600 text-sm">
                  Quickly find any contact with our powerful search.
                </p>
              </button>
              <button 
                onClick={() => setShowOrganizations(true)}
                className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow text-left"
              >
                <Building2 className="w-6 h-6 text-blue-500 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Organizations</h3>
                <p className="text-gray-600 text-sm">
                  View and manage company relationships.
                </p>
              </button>
              <button 
                onClick={() => setShowRecentActivity(true)}
                className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow text-left"
              >
                <Shield className="w-6 h-6 text-blue-500 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Recent Activity</h3>
                <p className="text-gray-600 text-sm">
                  See your latest contact updates and changes.
                </p>
              </button>
            </div>
          )}

          {/* AI Assistant Section */}
          {hasProfile && (
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-8 text-white">
              <div className="flex items-center space-x-4 mb-6">
                <Sparkles className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Your AI Contact Assistant</h2>
              </div>
              <p className="text-lg mb-6">
                I can help you with:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>Contact organization and cleanup</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>Finding duplicate contacts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>Updating contact information</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>Contact relationship insights</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {showCompanyForm && (
        <CompanySetupForm
          onComplete={handleCompanySetupComplete}
          onSkip={handleSkipSetup}
        />
      )}
      
      {showContactForm && (
        <ContactForm
          onClose={() => setShowContactForm(false)}
          onSuccess={() => {
            setShowContactForm(false);
            setRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
      
      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold">Search Contacts</h2>
              <button onClick={() => setShowSearch(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex space-x-2 mb-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, phone, or organization..."
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  disabled={!searchTerm.trim() || searchLoading}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="space-y-3">
                    {searchResults.map(contact => (
                      <div 
                        key={contact.id} 
                        className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedContact(contact);
                          setShowSearch(false);
                        }}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <User className="w-4 h-4 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {contact.first_name} {contact.last_name}
                            </h4>
                            {contact.title && (
                              <p className="text-sm text-gray-500">{contact.title}</p>
                            )}
                            {contact.organization && (
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <Building2 className="w-3 h-3 mr-1" />
                                {contact.organization.name}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {contact.emails?.slice(0, 1).map(email => (
                                <span key={email.id} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center">
                                  <Mail className="w-3 h-3 mr-1" />
                                  {email.email}
                                </span>
                              ))}
                              {contact.phones?.slice(0, 1).map(phone => (
                                <span key={phone.id} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full flex items-center">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {phone.phone}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchTerm && !searchLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No contacts found matching "{searchTerm}"</p>
                  </div>
                ) : !searchTerm ? (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Enter a search term to find contacts</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Organizations Modal */}
      {showOrganizations && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold">Organizations</h2>
              <button onClick={() => setShowOrganizations(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {orgLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-gray-500">Loading organizations...</p>
                </div>
              ) : organizations.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {organizations.map(org => (
                    <div key={org.id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <div 
                          className="bg-blue-100 p-2 rounded-full cursor-pointer"
                          onClick={() => setEditingOrganizationId(org.id)}
                        >
                          <Building2 className="w-4 h-4 text-blue-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{org.name}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full capitalize">
                              {org.type}
                            </span>
                            {org.domain && (
                              <span className="text-xs text-gray-500">{org.domain}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No organizations found</p>
                  <p className="text-sm mt-1">Organizations will appear here when you add contacts with company information</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Recent Activity Modal */}
      {showRecentActivity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold">Recent Activity</h2>
              <button onClick={() => setShowRecentActivity(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {recentActivity.length > 0 ? (
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  {recentActivity.map(activity => (
                    <div key={activity.id} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          {activity.type === 'contact_added' && <User className="w-4 h-4 text-blue-500" />}
                          {activity.type === 'contact_updated' && <User className="w-4 h-4 text-green-500" />}
                          {activity.type === 'organization_added' && <Building2 className="w-4 h-4 text-purple-500" />}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{activity.title}</h4>
                          <p className="text-sm text-gray-600">{activity.description}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-2">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatTimeAgo(activity.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No recent activity</p>
                  <p className="text-sm mt-1">Activity will appear here as you use Contact Manager</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {selectedContact && (
        <ContactDetails
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onEdit={() => {
            setEditingContactId(selectedContact.id);
            setSelectedContact(null);
          }}
        />
      )}
      
      {editingContactId && (
        <EditContactForm
          contactId={editingContactId}
          onClose={() => setEditingContactId(null)}
          onSuccess={() => {
            setEditingContactId(null);
            setRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
      
      {editingOrganizationId && (
        <EditOrganizationForm
          organization={organizations.find(org => org.id === editingOrganizationId)!}
          onClose={() => setEditingOrganizationId(null)}
          onSuccess={() => {
            setEditingOrganizationId(null);
            setRefreshTrigger(prev => prev + 1);
            loadOrganizations(); // Refresh organizations list
          }}
        />
      )}
    </div>
  );
}